import {
  buildFolderUploadTreeFromFileList,
  traverseDirectoryHandleTree,
  type FileSystemDirectoryHandleLike,
} from "@/utils/traverseDataTransferItems";
import { useCallback, useRef } from "react";
import { useCanvasFolderIngest } from "./useCanvasFolderIngest";

/**
 * Insert menu "Upload Folder". Owns the persistent hidden `webkitdirectory`
 * input (rendered in `CanvasPage`, outside any overlay) and lays the picked
 * folder out exactly as dropping it on the board would.
 *
 * A native folder-mode dialog where available (Chromium); Safari/Firefox fall
 * back to the input. The anchor is captured when the picker opens, not when it
 * resolves — the menu closes and its cursor clears while the dialog is up.
 */
export function useCanvasFolderUpload(
  canvasId: string | undefined,
  getFallbackPosition: () => { x: number; y: number },
) {
  const folderInputRef = useRef<HTMLInputElement>(null);
  const anchorRef = useRef<{ x: number; y: number } | null>(null);
  const { ingestFolderTree } = useCanvasFolderIngest(canvasId);

  const pickFolderAt = useCallback(
    (at: { x: number; y: number }) => {
      anchorRef.current = at;
      const picker = (
        window as unknown as {
          showDirectoryPicker?: () => Promise<FileSystemDirectoryHandleLike>;
        }
      ).showDirectoryPicker;
      if (!picker) {
        folderInputRef.current?.click();
        return;
      }
      void (async () => {
        try {
          const dir = await picker.call(window);
          await ingestFolderTree(await traverseDirectoryHandleTree(dir), at);
        } catch {
          // AbortError — the user cancelled the dialog.
        }
      })();
    },
    [ingestFolderTree],
  );

  const handleFolderSelect = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (!files || files.length === 0) return;
      const tree = buildFolderUploadTreeFromFileList(files);
      // Reset the input synchronously so re-picking the same folder fires change.
      event.target.value = "";
      // A pick that outlived its anchor (or never had one) lands centered.
      const at = anchorRef.current ?? getFallbackPosition();
      anchorRef.current = null;
      await ingestFolderTree(tree, at);
    },
    [ingestFolderTree, getFallbackPosition],
  );

  return { folderInputRef, pickFolderAt, handleFolderSelect };
}
